import React, { memo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import {
  QuestionPaperSection,
} from '../../services/generateQuestionPaperService';
import {
  MarksDisplayMode,
  PaperNumberingConfig,
} from '../../utils/paperNumbering';
import PaperPreview from './PaperPreview';

interface PaperFormData {
  classId: string;
  subjectId: string;
  title: string;
  description: string;
  instructions: string;
  totalMarks: number;
  duration: number;
  examDate?: string;
  textDirection: 'ltr' | 'rtl';
  marksDisplayMode: MarksDisplayMode;
  numbering: PaperNumberingConfig;
}

interface ReviewStepProps {
  paperForm: PaperFormData;
  sections: QuestionPaperSection[];
  isViewMode: boolean;
  currentMarks: number;
}

/**
 * Step 3 — final review before publish.
 *
 * Two surfaces, stacked:
 *   1. A publish-gate strip at the top (success / warning / error depending
 *      on marks matching + non-empty sections). Keeps the publish-time
 *      validation visible without opening the Alert on every edit.
 *   2. `PaperPreview` — WYSIWYG render that mirrors the PDF 1:1. Any paper
 *      edit the user made is reflected here immediately, so "Preview" is
 *      always honest.
 *
 * Save Draft / Publish buttons live on the parent stepper's footer — this
 * component is purely the review surface.
 */
const ReviewStep = memo<ReviewStepProps>(({
  paperForm,
  sections,
  isViewMode,
  currentMarks,
}) => {
  const marksMismatch = paperForm.totalMarks > 0 && currentMarks !== paperForm.totalMarks;

  return (
    <Box>
      {/* Publish gate — hidden in view mode (paper is already published / archived). */}
      {!isViewMode && (
        <Box sx={{ mb: 3 }}>
          {marksMismatch ? (
            <Alert severity="error">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Marks Mismatch
              </Typography>
              <Typography variant="body2">
                Current marks ({currentMarks}) do not match total marks ({paperForm.totalMarks}).
                You cannot publish until the marks match — adjust question marks or update the total.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info">
              Review your question paper below. Save as a draft to keep editing, or publish when you're ready.
            </Alert>
          )}
        </Box>
      )}

      {isViewMode && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Paper Preview
        </Typography>
      )}

      <PaperPreview
        paperForm={paperForm}
        sections={sections}
        currentMarks={currentMarks}
      />
    </Box>
  );
});

ReviewStep.displayName = 'ReviewStep';

export default ReviewStep;
